// React imports
import React from 'react';

// External library imports
import {
  Box,
  Slider,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';

/**
 * Props interface for RatingInput component
 */
interface RatingInputProps {
  /** Type of rating input to display */
  ratingType: 'numerical' | 'likert' | 'boolean' | undefined;
  /** Current rating value (0-5 scale) */
  value: number | null;
  /** Callback function called when rating changes */
  onChange: (value: number) => void;
  /** Whether the input is disabled */
  disabled?: boolean;
}

/**
 * RatingInput Component
 * 
 * A flexible rating input component that supports three rating types:
 * - Numerical: Slider input (0-5 scale with 0.1 increments)
 * - Likert: Radio buttons (1-5 scale with text labels)
 * - Boolean: Toggle buttons (Yes/No)
 * 
 * @param props - RatingInputProps
 * @returns Rating input component based on ratingType
 */
const RatingInput: React.FC<RatingInputProps> = ({
  ratingType = 'numerical',
  value,
  onChange,
  disabled = false,
}) => {
  // Numerical: Slider (0-5)
  if (ratingType === 'numerical') {
    return (
      <Box sx={{ px: 2 }}>
        <Slider
          value={value || 0}
          onChange={(_, val) => onChange(Array.isArray(val) ? val[0] : val)}
          min={0}
          max={5}
          step={0.1}
          marks={[
            { value: 0, label: '0' },
            { value: 1, label: '1' },
            { value: 2, label: '2' },
            { value: 3, label: '3' },
            { value: 4, label: '4' },
            { value: 5, label: '5' },
          ]}
          valueLabelDisplay="on"
          disabled={disabled}
          sx={{
            '& .MuiSlider-valueLabel': {
              fontSize: 14,
              fontWeight: 'bold',
              backgroundColor: 'primary.main',
            },
            '& .MuiSlider-mark': {
              backgroundColor: 'grey.400',
            },
            '& .MuiSlider-markLabel': {
              fontSize: 12,
            },
          }}
        />
      </Box>
    );
  }

  // Likert: Radio buttons (1-5) - Performance ratings
  if (ratingType === 'likert') {
    const likertLabels = [
      { value: 5, label: 'Outstanding' },
      { value: 4, label: 'Exceeds Expectations' },
      { value: 3, label: 'Meets Expectations' },
      { value: 2, label: 'Needs Improvement' },
      { value: 1, label: 'Unacceptable' },
    ];

    return (
      <FormControl component="fieldset" disabled={disabled} fullWidth>
        <RadioGroup
          row
          value={value || ''}
          onChange={(e) => onChange(parseInt(e.target.value))}
          sx={{ flexWrap: 'wrap', gap: 0, rowGap: 0.5 }}
        >
          {likertLabels.map((option) => (
            <FormControlLabel
              key={option.value}
              value={option.value}
              control={<Radio size="small" />}
              label={
                <Typography variant="body2" sx={{ fontWeight: '600', fontSize: '0.85rem', color: 'text.primary' }}>
                  {option.label}
                </Typography>
              }
              sx={{ m: 0, minWidth: 'max-content', pr: 1.5 }}
            />
          ))}
        </RadioGroup>
      </FormControl>
    );
  }

  // Boolean: Toggle buttons (Yes/No)
  if (ratingType === 'boolean') {
    const handleBooleanChange = (_: React.MouseEvent<HTMLElement>, newValue: string | null) => {
      if (newValue !== null) {
        onChange(newValue === 'yes' ? 5 : 0);
      }
    };

    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%', gap: 2 }}>
        <ToggleButtonGroup
          value={value === 5 ? 'yes' : value === 0 ? 'no' : null}
          exclusive
          onChange={handleBooleanChange}
          disabled={disabled}
          sx={{ 
            width: '100%', 
            maxWidth: '450px',
            gap: 1.5,
            '& .MuiToggleButtonGroup-grouped': {
              border: 'none',
              '&:not(:first-of-type)': {
                borderRadius: '12px',
              },
              '&:first-of-type': {
                borderRadius: '12px',
              },
            },
          }}
        >
          <ToggleButton 
            value="yes" 
            sx={{ 
              px: 5, 
              py: 2, 
              flex: 1,
              borderRadius: '12px !important',
              border: '2px solid',
              borderColor: value === 5 ? 'success.main' : 'grey.300',
              backgroundColor: value === 5 ? 'success.main' : 'white',
              color: value === 5 ? 'white' : 'text.primary',
              fontWeight: '600',
              fontSize: '1rem',
              textTransform: 'none',
              boxShadow: value === 5 
                ? '0 4px 12px rgba(46, 125, 50, 0.3)' 
                : '0 2px 4px rgba(0, 0, 0, 0.1)',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              '&:hover': {
                backgroundColor: value === 5 ? 'success.dark' : 'success.light',
                color: 'white',
                borderColor: 'success.main',
                boxShadow: value === 5 
                  ? '0 6px 16px rgba(46, 125, 50, 0.4)' 
                  : '0 4px 8px rgba(46, 125, 50, 0.2)',
                transform: 'translateY(-2px)',
              },
              '&.Mui-selected': {
                backgroundColor: 'success.main',
                color: 'white',
                borderColor: 'success.main',
                '&:hover': {
                  backgroundColor: 'success.dark',
                },
              },
            }}
          >
            <Typography variant="body1" sx={{ fontWeight: '600', fontSize: '1rem' }}>
              Yes
            </Typography>
          </ToggleButton>
          <ToggleButton 
            value="no" 
            sx={{ 
              px: 5, 
              py: 2, 
              flex: 1,
              borderRadius: '12px !important',
              border: '2px solid',
              borderColor: value === 0 ? 'error.main' : 'grey.300',
              backgroundColor: value === 0 ? 'error.main' : 'white',
              color: value === 0 ? 'white' : 'text.primary',
              fontWeight: '600',
              fontSize: '1rem',
              textTransform: 'none',
              boxShadow: value === 0 
                ? '0 4px 12px rgba(211, 47, 47, 0.3)' 
                : '0 2px 4px rgba(0, 0, 0, 0.1)',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              '&:hover': {
                backgroundColor: value === 0 ? 'error.dark' : 'error.light',
                color: 'white',
                borderColor: 'error.main',
                boxShadow: value === 0 
                  ? '0 6px 16px rgba(211, 47, 47, 0.4)' 
                  : '0 4px 8px rgba(211, 47, 47, 0.2)',
                transform: 'translateY(-2px)',
              },
              '&.Mui-selected': {
                backgroundColor: 'error.main',
                color: 'white',
                borderColor: 'error.main',
                '&:hover': {
                  backgroundColor: 'error.dark',
                },
              },
            }}
          >
            <Typography variant="body1" sx={{ fontWeight: '600', fontSize: '1rem' }}>
              No
            </Typography>
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>
    );
  }

  // Default fallback to numerical
  return (
    <Box sx={{ px: 2 }}>
      <Slider
      value={value || 0}
      onChange={(_, val) => onChange(Array.isArray(val) ? val[0] : val)}
      min={0}
      max={5}
      step={0.1}
      marks={[
          { value: 0, label: '0' },
          { value: 1, label: '1' },
          { value: 2, label: '2' },
          { value: 3, label: '3' },
          { value: 4, label: '4' },
          { value: 5, label: '5' },
        ]}
        valueLabelDisplay="on"
        disabled={disabled}
        sx={{
          '& .MuiSlider-valueLabel': {
            fontSize: 14,
            fontWeight: 'bold',
            backgroundColor: 'primary.main',
          },
        }}
      />
    </Box>
  );
};

export default RatingInput;

